import { applyTax } from './tax.js';
// The report's branch filter (סניף) — a VIEW over the P&L-unit lines. A branch
// is an Arbox location inside a unit. Picking one shows each line's share of
// that branch; the line itself is still computed, sent, approved and closed per
// (coach, unit). Nothing here changes money.
/** Filter value for the pay that belongs to no branch: base salary, travel
 *  that isn't per-km, the volume and conversion ladders, the one-time bonus
 *  and deduction, and classes Arbox recorded without a location. */
export const NO_BRANCH = '__no_branch__';
export const NO_BRANCH_LABEL = 'ללא שיוך לסניף';
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
/** The money fields a branch view recomputes. Every other field is the line's. */
const MONEY_FIELDS = [
    'class_pay',
    'weekend_bonus',
    'class_bonus',
    'volume_bonus',
    'daily_class_adjustment',
    'conversion_bonus',
    'base_salary',
    'travel',
    'empty_class_pay',
    'cancelled_class_pay',
    'one_time_bonus',
    'deduction',
    'subtotal',
    'vat',
    'total',
    'withholding',
    'net_total',
];
const COUNT_FIELDS = [
    'registration_count',
    'checkin_count',
    'late_cancel_count',
    'workdays_count',
    'weekend_workdays_count',
    'class_count',
    'empty_class_count',
    'cancelled_class_count',
];
/** The row as one branch's share: only that branch's per-meeting pay, taxed
 *  with the line's own tax profile. */
function branchShare(row, b) {
    const pre = round2(b.class_pay +
        b.weekend_bonus +
        b.class_bonus +
        b.daily_class_adjustment +
        b.empty_class_pay +
        b.cancelled_class_pay +
        b.travel);
    const tax = applyTax(pre, {
        tax_type: row.tax_type,
        vat_mode: row.vat_mode,
        withholding_pct: row.withholding_pct,
    });
    return {
        ...row,
        class_pay: b.class_pay,
        weekend_bonus: b.weekend_bonus,
        class_bonus: b.class_bonus,
        volume_bonus: 0,
        daily_class_adjustment: b.daily_class_adjustment,
        conversion_bonus: 0,
        base_salary: 0,
        travel: b.travel,
        empty_class_pay: b.empty_class_pay,
        cancelled_class_pay: b.cancelled_class_pay,
        one_time_bonus: 0,
        deduction: 0,
        ...tax,
        registration_count: b.registration_count,
        checkin_count: b.checkin_count,
        late_cancel_count: b.late_cancel_count,
        workdays_count: b.workdays_count,
        weekend_workdays_count: b.weekend_workdays_count,
        class_count: b.class_count,
        empty_class_count: b.empty_class_count,
        cancelled_class_count: b.cancelled_class_count,
        is_zero: tax.total === 0,
    };
}
/** Whether the row carries a branch breakdown. Lines frozen before the filter
 *  existed (and the multi-mechanism lines) don't, and can't be split. */
export function hasBranchBreakdown(row) {
    return Array.isArray(row.by_location);
}
/**
 * The row as seen through the branch filter, or null when the row has nothing
 * to show for that branch (no breakdown, or the coach never taught there).
 *
 * The NO_BRANCH share is the line MINUS every branch share, field by field, so
 * the branches plus NO_BRANCH always add up to the line exactly — VAT rounding
 * included. Its counts are 0: the activity is all in the branch shares.
 */
export function rowForBranch(row, branch) {
    const slices = row.by_location;
    if (!slices)
        return null;
    if (branch !== NO_BRANCH) {
        const b = slices.find((s) => s.location === branch);
        return b ? branchShare(row, b) : null;
    }
    const rest = { ...row };
    for (const b of slices) {
        const share = branchShare(row, b);
        for (const f of MONEY_FIELDS)
            rest[f] = round2((rest[f] ?? 0) - (share[f] ?? 0));
    }
    for (const f of COUNT_FIELDS)
        rest[f] = 0;
    rest.is_zero = rest.total === 0;
    return rest;
}
/** Every branch name across the rows, by name. */
export function branchesOf(rows) {
    const names = new Set();
    for (const r of rows)
        for (const b of r.by_location ?? [])
            names.add(b.location);
    return [...names].sort((a, b) => a.localeCompare(b, 'he'));
}
//# sourceMappingURL=branch-view.js.map